import { Col, Collapse, Form, Row, Button } from 'antd';
import classNames from 'classnames';
import { Spinal } from 'components/spinal/Spinal';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { CardTable } from '../../../../components/card-table';
import { roundAndDisplayWithDecimals } from '../../../../helpers/logica/round-and-display-with-decimals';
import { achtergrondInfo } from '../../bodem-overig/info/achtergrondInfo';
import { PercelenModel } from '../models/percelenMock';
import styles from '../styles.module.scss';
import { PerceelRow } from './components/perceelRow';
const leftWithPadding = classNames(['text-left', styles.paddingRight]);
const paddingLeftRight = classNames([styles.paddingLeft, styles.paddingRight]);
const boldTitleLeft = classNames([styles.fontWeight500, styles.paddingRight, 'text-left']);
const boldTitle = classNames([styles.fontWeight500, styles.paddingRight]);

const { Panel } = Collapse;
const info = (fosfaattoestandCategorieen: PercelenModel['fosfaattoestandCategorieen'] = []) => (
  <>
    <div>
      <p>
        Na gebruik van de koppeltool waarmee gewaspercelen en bodemanalyses worden gekoppeld ziet u hier het overzicht van uw gekoppelde gewaspercelen
        (alfabetisch op naam), met gegevens die van belang zijn voor de KringloopWijzer.
      </p>
      <p>
        Als u onder het kopje perceelaanduiding op het {'->'} klikt dan ziet u de gekoppelde bodemanalyse(s). Indien u onder ket kopje gewas op het blauwe X
        klikt kunt u geoogst gewas invoeren. Wat u heeft geoogst kan afwijken van uw registratie bij RVO.
      </p>
      <p>
        U kunt bij graspercelen het % klaver invullen. Het programma berekent het areaal met klaver en de bezettingsgraad. Ook kunt u aangeven of een
        gewasperceel fosfaat fixerend is als het aan voorwaarden voldoet en u kunt nog aangeven of gewaspercelen wel/niet meegenomen dienen te worden in de
        berekening als gegevens naar uw mening niet juist zijn. Indien nodig kunt u onder handmatige percelen juiste gegevens invoeren.
      </p>
      <p>
        Ook landschapselementen e.d. worden door RVO als gewaspercelen geleverd. Deze gewaspercelen worden wel getoond in de lijst, waarbij het vinkje meenemen
        in berekening dan niet is aangevinkt en ze staan onderaan de lijst. Voor de KringloopWijzer zijn eigenlijk alleen de gewassen waar RVO mestnormen bij
        aangeeft van belang. U kunt dergelijke percelen wel meenemen in de berekening en deze worden dan onder akkerbouw (overige gewassen) verwerkt.
      </p>
      {achtergrondInfo(fosfaattoestandCategorieen)}
    </div>
  </>
);

interface GewasPercelenProps {
  isKlwReadOnly: boolean;
  isLoading: boolean;
  gebruikKoppeltoolKoolstofBodem: boolean;
  gebruikKoppeltool: boolean;
}
export const GewasPercelen: FC<GewasPercelenProps> = ({ isKlwReadOnly, isLoading, gebruikKoppeltoolKoolstofBodem, gebruikKoppeltool }) => {
  // Since not all properties fit in table, we toggle between showing fosfaat or koolstof
  enum ShownGroup {
    Fosfaat = 'fosfaat',
    Koolstof = 'koolstof',
  }
  const [shownGroup, setShownGroup] = useState<ShownGroup>(gebruikKoppeltool ? ShownGroup.Fosfaat : ShownGroup.Koolstof);

  return (
    <CardTable>
      <CardTable.Header>
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => {
            return (
              prev.fosfaattoestandCategorieen !== cur.fosfaattoestandCategorieen ||
              prev.gebruikKoppeltool !== cur.gebruikKoppeltool ||
              prev.gebruikKoppeltoolKoolstofBodem !== cur.gebruikKoppeltoolKoolstofBodem
            );
          }}>
          {({ getFieldValue }) => {
            const fosfaattoestandCategorieen = getFieldValue(['fosfaattoestandCategorieen']);

            const gebruikKoppeltool: PercelenModel['gebruikKoppeltool'] = getFieldValue(['gebruikKoppeltool']);
            const gebruikKoppeltoolKoolstofBodem: PercelenModel['gebruikKoppeltoolKoolstofBodem'] = getFieldValue(['gebruikKoppeltoolKoolstofBodem']);

            if (gebruikKoppeltool || gebruikKoppeltoolKoolstofBodem) {
              if (!gebruikKoppeltool && shownGroup === ShownGroup.Fosfaat) {
                setShownGroup(ShownGroup.Koolstof);
              } else if (!gebruikKoppeltoolKoolstofBodem && shownGroup === ShownGroup.Koolstof) {
                setShownGroup(ShownGroup.Fosfaat);
              }
            }

            return (
              <CardTable.Header.Title infoContent={info(fosfaattoestandCategorieen)}>
                <Row className={paddingLeftRight} gutter={12}>
                  <Col span={12}>RVO Gewaspercelen</Col>
                  <Col span={12}>
                    <div
                      style={{
                        paddingRight: 40,
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}>
                      {gebruikKoppeltool && gebruikKoppeltoolKoolstofBodem && (
                        <Button
                          type="primary"
                          shape="round"
                          onClick={() => {
                            setShownGroup(shownGroup === ShownGroup.Fosfaat ? ShownGroup.Koolstof : ShownGroup.Fosfaat);
                          }}>
                          {shownGroup === ShownGroup.Fosfaat ? 'Toon Koolstof' : 'Toon Fosfaat'}
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardTable.Header.Title>
            );
          }}
        </Form.Item>

        <CardTable.Header.ColumnTitles>
          <Row className={paddingLeftRight} gutter={12}>
            <Col span={3}>Perceelaanduiding</Col>
            <Col className="text-left" span={3}>
              Gewascode + gewas
            </Col>
            <Col className="text-left" span={2}>
              Grondsoort
            </Col>
            <Col className="text-left" span={2}>
              Grondwatertrap
            </Col>
            <Col span={2}>
              Oppervlakte
              <CardTable.ColumnUnit>[ha]</CardTable.ColumnUnit>
            </Col>
            {shownGroup === 'fosfaat' && gebruikKoppeltool ? (
              <>
                <Col span={2}>P-Al</Col>
                <Col span={2}>Pw</Col>
                <Col span={2}>P-PAE</Col>
                <Col span={2}>Fixerend</Col>
                <Col span={2}>Meenemen in berekening</Col>
                <Col span={2}>Fosfaatklasse bepaling</Col>
              </>
            ) : shownGroup === 'koolstof' && gebruikKoppeltoolKoolstofBodem ? (
              <>
                <Col span={3}>
                  SOC<CardTable.ColumnUnit>[%]</CardTable.ColumnUnit>
                </Col>
                <Col span={3}>
                  Organische stof
                  <CardTable.ColumnUnit>[%]</CardTable.ColumnUnit>
                </Col>
                <Col span={3}>
                  Lutum<CardTable.ColumnUnit>[%]</CardTable.ColumnUnit>
                </Col>
                <Col span={3}>C/N</Col>
              </>
            ) : null}
          </Row>
        </CardTable.Header.ColumnTitles>
      </CardTable.Header>
      <CardTable.Body>
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => {
            return prev.gekoppeldeGewaspercelen !== cur.gekoppeldeGewaspercelen;
          }}>
          {({ getFieldValue }) => {
            const gekoppeldeGewaspercelen = getFieldValue(['gekoppeldeGewaspercelen']);
            return (
              <Spinal initializing={!gekoppeldeGewaspercelen} loading={isLoading}>
                <Form.List name={['gekoppeldeGewaspercelen']}>
                  {fields => (
                    <>
                      <Collapse expandIcon={() => null}>
                        {fields?.map((field, index) => (
                          <>
                            <Panel
                              key={index}
                              header={
                                <PerceelRow
                                  isKlwReadOnly={isKlwReadOnly}
                                  index={index}
                                  perceel={gekoppeldeGewaspercelen[index]}
                                  shownGroup={shownGroup}
                                  gebruikKoppeltoolKoolstofBodem={gebruikKoppeltoolKoolstofBodem}
                                  gebruikKoppeltool={gebruikKoppeltool}
                                />
                              }>
                              <Row className={styles.marginBottom5}>
                                <Col
                                  span={3}
                                  style={{
                                    paddingRight: 16,
                                  }}
                                />
                                <Col className={boldTitleLeft} span={3}>
                                  Topografisch perceel
                                </Col>
                                <Col className={boldTitleLeft} span={2}>
                                  Bodemanalyse naam
                                </Col>
                                <Col className={boldTitleLeft} span={2}>
                                  Datum
                                </Col>
                                <Col className={boldTitle} span={2}>
                                  Laboratorium
                                </Col>
                                {shownGroup === 'fosfaat' && gebruikKoppeltool ? (
                                  <>
                                    <Col className={boldTitle} span={2}>
                                      P-Al
                                    </Col>
                                    <Col className={boldTitle} span={2}>
                                      Pw
                                    </Col>
                                    <Col className={boldTitle} span={2}>
                                      P-PAE
                                    </Col>
                                    <Col span={2} />
                                    <Col span={2} />
                                    <Col span={2} />
                                  </>
                                ) : shownGroup === 'koolstof' && gebruikKoppeltoolKoolstofBodem ? (
                                  <>
                                    <Col className={boldTitle} span={3}>
                                      SOC
                                    </Col>
                                    <Col className={boldTitle} span={3}>
                                      Organische stof
                                    </Col>
                                    <Col className={boldTitle} span={3}>
                                      Lutum
                                    </Col>
                                    <Col className={boldTitle} span={3}>
                                      C/N
                                    </Col>
                                  </>
                                ) : null}
                              </Row>
                              {gekoppeldeGewaspercelen[index]?.isGemonsterd && (
                                <>
                                  {gekoppeldeGewaspercelen[index].monsters?.map((monster, index) => (
                                    <Row key={index}>
                                      <Col span={3} />
                                      <Col className={leftWithPadding} span={3}>
                                        {gekoppeldeGewaspercelen[index].rvoObjectId}
                                      </Col>
                                      <Col className={leftWithPadding} span={2}>
                                        {monster.monsterNaam}
                                      </Col>
                                      <Col className={leftWithPadding} span={2}>
                                        {dayjs(monster.monsterDatum).format('DD-MM-YYYY')}
                                      </Col>
                                      <Col className={styles.paddingRight} span={2}>
                                        {monster.laboratoriumNaam}
                                      </Col>

                                      {shownGroup === 'fosfaat' && gebruikKoppeltool ? (
                                        <>
                                          <Col className={styles.paddingRight} span={2}>
                                            {roundAndDisplayWithDecimals(2)(monster.pal)}
                                          </Col>
                                          <Col className={styles.paddingRight} span={2}>
                                            {roundAndDisplayWithDecimals(2)(monster.pw)}
                                          </Col>
                                          <Col className={styles.paddingRight} span={2}>
                                            {roundAndDisplayWithDecimals(2)(monster.ppae)}
                                          </Col>
                                          <Col span={2} />
                                          <Col span={2} />
                                          <Col span={2} />
                                        </>
                                      ) : shownGroup === 'koolstof' && gebruikKoppeltoolKoolstofBodem ? (
                                        <>
                                          <Col className={styles.paddingRight} span={3}>
                                            {roundAndDisplayWithDecimals(2)(monster.soc)}
                                          </Col>
                                          <Col className={styles.paddingRight} span={3}>
                                            {roundAndDisplayWithDecimals(2)(monster.organischeStof)}
                                          </Col>
                                          <Col className={styles.paddingRight} span={3}>
                                            {roundAndDisplayWithDecimals(2)(monster.lutum)}
                                          </Col>
                                          <Col className={styles.paddingRight} span={3}>
                                            {roundAndDisplayWithDecimals(2)(monster.cnRatio)}
                                          </Col>
                                        </>
                                      ) : null}
                                    </Row>
                                  ))}
                                </>
                              )}
                            </Panel>
                          </>
                        ))}
                      </Collapse>{' '}
                    </>
                  )}
                </Form.List>
              </Spinal>
            );
          }}
        </Form.Item>
      </CardTable.Body>
    </CardTable>
  );
};
